import { useState } from 'react'
import InstrumentStrip, {
  type Readout,
} from '../../surfaces/desk/parts/InstrumentStrip'
import Traffic, { type TrafficRow } from '../../surfaces/desk/parts/Traffic'
import Outbox from '../../surfaces/desk/parts/Outbox'
import OpeningRun from '../../components/OpeningRun'
import Boot from '../../surfaces/desk/parts/Boot'
import Dossier, {
  cardsAsGiven,
  type ReceivedItem,
} from '../../surfaces/desk/parts/Dossier'
import type { Opening } from '../../lib/contract'
import styles from './DeskHarness.module.css'

/**
 * The desk harness — dev only (desk-grammar brief §0.5, §3, acceptance 9).
 *
 * Renders the desk's parts against MOCK data so the [CONTRACT PENDING] surfaces
 * can be built and judged before the server emits them. Registered only under
 * `import.meta.env.DEV`, so none of this reaches the production bundle.
 *
 * Nothing here is wired to a session. It exists to answer "does the grammar
 * read right", which is not a question the live surface can answer while the
 * telemetry contract is empty.
 */

const t = (h: number, m: number, s: number) =>
  new Date(Date.UTC(2026, 8, 22, h, m, s))

/** Every instrument the brief names, as they will look once the contract lands. */
const FULL_STRIP: Readout[] = [
  { key: 'clock', label: 'desk', value: '11:24' },
  { key: 'sked', label: 'next sked', value: '13:00', hint: 'in 96m' },
  { key: 'depth', label: 'depth', value: '84 m' },
  { key: 'heart', label: 'heart', value: '96 bpm' },
  { key: 'gas', label: 'gas', value: '20.4 / 0.4 / 79.2' },
  { key: 'geo', label: 'geophone', value: '11:02 · single, sharp' },
  { key: 'charge', label: 'charge', value: '61%' },
  { key: 'node', label: 'node', value: 'N-4' },
]

/** What the live surface actually shows today: two readouts, the rest absent. */
const LIVE_STRIP: Readout[] = [
  { key: 'clock', label: 'desk', value: '11:24' },
  { key: 'sked', label: 'next sked', value: '13:00', hint: 'in 96m' },
]

/** A stated condition rather than a measurement — his absence speaking. */
const STATED_STRIP: Readout[] = [
  { key: 'clock', label: 'desk', value: '11:24' },
  { key: 'heart', label: 'heart', value: 'strap off', stated: true },
  { key: 'depth', label: 'depth', value: '84 m' },
]

type Overlay = null | 'opening' | 'boot-arranged' | 'boot-blind' | 'dossier'

/**
 * A stand-in opening run. Shaped like the wire, not lifted from Silfurberg: the
 * live surface renders the real pack's cards, and a harness that copied them
 * would quietly become a second place the pack's copy lives.
 */
const MOCK_OPENING: Opening = {
  action: 'OPEN THE LINK',
  cards: [
    {
      id: 'title',
      kind: 'title',
      title: 'A WORKING NAME',
      body: '*the subtitle sits here*\n\nA place · A month',
    },
    {
      id: 'note',
      kind: 'paper',
      title: 'A note, sent ahead',
      body: 'i need a desk.\n\nten days under. i work below, you hold the surface: the watch, the log, the papers i cannot reach from down there.\n\ni do not need someone to tell me to be careful. i need someone who writes down what i say.',
    },
    {
      /*
       * A PLAIN card, so the sparse kind can be judged beside the dense ones — which is the
       * whole comparison it exists for. Harness copy, deliberately generic: no pack's words
       * live here (see the note above this manifest).
       */
      id: 'plain',
      kind: 'plain',
      title: 'ONE THING TO KNOW',
      body: 'No one has been down there since the war.',
    },
    {
      id: 'sheet',
      kind: 'paper',
      title: 'One sheet',
      body: 'Worked, then closed, then sealed. The records were split between two offices and one of them burned.\n\nNo reliable plan of the workings exists.',
    },
  ],
}

/**
 * [CONTRACT PENDING] — there is no receiving-event shape on the wire yet, so
 * this is the only place the received flow can be judged. Provenance is written
 * as the FEED would write it, because the client never composes one.
 */
const MOCK_RECEIVED: ReceivedItem[] = [
  {
    id: 'r1',
    title: 'Assay, the south drift',
    provenance: 'carried out by K., day 9',
    at: '30 Sep · 18:20',
    body: 'Three samples off the south face.\n\nTwo are spar, clean. The third is not spar at all.',
  },
]

const ROWS: TrafficRow[] = [
  {
    id: '0',
    kind: 'rx',
    register: 'Protocol',
    text: 'sked open 10:41 — link established, node N-1',
    at: t(10, 41, 0),
    burstStart: true,
  },
  {
    id: '1',
    kind: 'rx',
    register: 'Voice',
    text: 'down the first pitch. rope is good. rock is drier than the photo said',
    at: t(10, 41, 12),
  },
  {
    id: '2',
    kind: 'rx',
    register: 'Voice',
    text: 'the old timber is still standing where the survey put it. someone knew what they were doing',
    at: t(10, 41, 27),
  },
  {
    // His composed second register — written, not spoken.
    id: '2b',
    kind: 'rx',
    register: 'Log',
    text: 'Level 1, east drift. Timber sound at 40 m, rotten past that. Water audible below and to the right. Air moving — something is open down there.',
    at: t(10, 41, 55),
  },
  {
    id: '3',
    kind: 'tx',
    register: 'Operator',
    text: 'copy. how much line have you got left?',
    at: t(10, 42, 3),
  },
  {
    id: '4',
    kind: 'rx',
    register: 'Voice',
    text: 'maybe forty metres. enough for the next pitch, not enough to be clever',
    at: t(10, 42, 40),
    burstStart: true,
  },
  {
    // The body channel is not speech — it never crossed the wire.
    id: '5',
    kind: 'rx',
    register: 'Voice',
    channel: 'body',
    text: 'his breathing is louder than the words',
    at: t(10, 42, 44),
  },
  { id: '6', kind: 'seam', text: '— 39 minutes pass at the desk —' },
  {
    id: '7',
    kind: 'rx',
    register: 'Voice',
    text: 'jæja. i am at the bottom of the second pitch and there is a door here. an actual door',
    at: t(11, 21, 9),
    burstStart: true,
  },
  {
    id: '8',
    kind: 'tx',
    register: 'Operator',
    text: 'a door? describe it — hinges, lock, any marking',
    at: t(11, 23, 0),
    pending: true,
  },
]

export default function DeskHarness() {
  const [strip, setStrip] = useState<'live' | 'full' | 'stated'>('live')
  const [standing, setStanding] = useState(false)
  const [overlay, setOverlay] = useState<Overlay>(null)

  const readouts =
    strip === 'full' ? FULL_STRIP : strip === 'stated' ? STATED_STRIP : LIVE_STRIP

  /*
   * The overlays take the whole surface, exactly as they do in play, so what is
   * judged here is what ships. Each is dismissed by its own real control — the
   * run by its closing action, the boot by any interaction, the dossier by its
   * close — so the harness is also where those controls get exercised.
   */
  if (overlay === 'opening') {
    return <OpeningRun opening={MOCK_OPENING} onComplete={() => setOverlay(null)} />
  }
  if (overlay === 'boot-arranged' || overlay === 'boot-blind') {
    return (
      <Boot
        shape={overlay === 'boot-arranged' ? 'arranged' : 'blind'}
        onDone={() => setOverlay(null)}
      />
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.controls}>
        <span className={styles.tag}>DESK HARNESS · dev only · mock data</span>
        <div className={styles.switches}>
          {(['live', 'full', 'stated'] as const).map((s) => (
            <button
              key={s}
              className={`${styles.switch} ${strip === s ? styles.on : ''}`}
              onClick={() => setStrip(s)}
            >
              {s === 'live' ? 'strip: live today' : `strip: ${s}`}
            </button>
          ))}
          <button
            className={`${styles.switch} ${standing ? styles.on : ''}`}
            onClick={() => setStanding((v) => !v)}
          >
            dark window
          </button>
          <button className={styles.switch} onClick={() => setOverlay('opening')}>
            opening run
          </button>
          <button className={styles.switch} onClick={() => setOverlay('boot-arranged')}>
            boot: arranged
          </button>
          <button className={styles.switch} onClick={() => setOverlay('boot-blind')}>
            boot: blind
          </button>
          <button
            className={`${styles.switch} ${overlay === 'dossier' ? styles.on : ''}`}
            onClick={() => setOverlay((v) => (v === 'dossier' ? null : 'dossier'))}
          >
            dossier
          </button>
        </div>
      </div>

      <div className={styles.desk}>
        {overlay === 'dossier' && (
          <Dossier
            given={cardsAsGiven(MOCK_OPENING.cards)}
            received={MOCK_RECEIVED}
            onClose={() => setOverlay(null)}
          />
        )}
        <InstrumentStrip readouts={readouts} />
        <Traffic
          rows={ROWS}
          working={false}
          standing={
            standing
              ? 'past N-4 · turnaround stated 90 min · last relay 11:21'
              : null
          }
        />
        <Outbox onSend={() => {}} />
      </div>
    </div>
  )
}
